import {
  Box,
  Popper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { IconCandidates, IconFlexPools, IconJobs, IconPartner } from 'assets/img';
import { theme, UserAvatar } from 'redesign';

type Claimant = {
  firstName: string;
  lastName: string;
  hasFlexPools: boolean;
  employmentType: string;
  imageUrl?: string;
  label?: string;
};

type PopoverData = {
  id: string;
  name: string;
  maxClaimants: number;
  acceptedClaimantsCount: number;
  flexpoolClaimantsCount: number;
  partnerClaimantsCount: number;
  claimants: Claimant[];
};

type ShiftPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  data: PopoverData | null;
  maxClaimants?: number;
};

export const ShiftPopover = ({ open, anchorEl, data, maxClaimants }: ShiftPopoverProps) => {
  if (!data) {
    return null;
  }

  return (
    <Popper id={data.id} open={open} anchorEl={anchorEl} placement="left" style={{ zIndex: 1300 }}>
      <StyledPopOver>
        <PopoverHeaderBox>
          <PopoverJobIconBox>
            <IconJobs width={30} height={30} />
          </PopoverJobIconBox>
          <Typography variant="h3" textOverflow="ellipsis" noWrap maxWidth={300}>
            {data.name}
          </Typography>
        </PopoverHeaderBox>
        <ShiftInfoRow sx={{ justifyContent: 'space-between' }}>
          <ShiftInfoBox>
            <IconFlexPools />
            <Typography variant="body2">{data.flexpoolClaimantsCount} Flexpool(s)</Typography>
          </ShiftInfoBox>
          <ShiftInfoBox pr={4}>
            <IconPartner />
            <Typography variant="body2">{data.partnerClaimantsCount} Partner(s)</Typography>
          </ShiftInfoBox>
        </ShiftInfoRow>
        <ShiftInfoRow>
          <IconCandidates />
          <Typography variant="body2">
            Flexwerker(s): {`${data.acceptedClaimantsCount}/${maxClaimants ?? data.maxClaimants}`}
          </Typography>
        </ShiftInfoRow>
        <Table>
          <TableBody>
            {data.claimants.slice(0, 5).map((claimant, index) => (
              <TableRow key={index}>
                <StyledTableCell sx={{ padding: 0 }}>
                  <UserAvatar
                    firstName={claimant.firstName}
                    lastName={claimant.lastName}
                    imageUrl={claimant.imageUrl}
                    avatarLabel={claimant.label}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <FwNameBox>
                    <Typography variant="body2" textOverflow="ellipsis" noWrap maxWidth={150}>
                      {claimant.firstName} {claimant.lastName}
                    </Typography>
                    {claimant.hasFlexPools && <IconFlexPools />}
                  </FwNameBox>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography variant="body2">{claimant.employmentType}</Typography>
                </StyledTableCell>
              </TableRow>
            ))}
            {data.claimants.length > 5 ? extraClaimantsRow() : null}
          </TableBody>
        </Table>
      </StyledPopOver>
    </Popper>
  );
};

const extraClaimantsRow = () => {
  return (
    <TableRow key={6}>
      {' '}
      <StyledTableCell sx={{ padding: '0px 12px' }}> </StyledTableCell>
      <StyledTableCell sx={{ padding: '0px 12px' }}>
        {' '}
        <Typography variant="body1">...</Typography>
      </StyledTableCell>
      <StyledTableCell sx={{ padding: '0px 12px' }}>
        {' '}
        <Typography variant="body1">...</Typography>
      </StyledTableCell>
    </TableRow>
  );
};

const StyledPopOver = styled(Box)(() => ({
  padding: '24px',
  backgroundColor: 'white',
  borderRadius: '24px',
  minWidth: '400px',
  boxShadow:
    '0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)',
}));

const PopoverHeaderBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  paddingLeft: '12px',
}));

const PopoverJobIconBox = styled(Box)(() => ({
  backgroundColor: theme.palette.lwPrimary['40'],
  borderRadius: '50%',
  width: '50px',
  height: '50px',
  padding: '10px',
}));

const ShiftInfoRow = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '4px',
  alignItems: 'center',
  marginTop: '12px',
  marginBottom: '12px',
  paddingLeft: '18px',
}));

const ShiftInfoBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '6px',
  alignItems: 'center',
}));

const FwNameBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const StyledTableCell = styled(TableCell)(() => ({
  padding: '12px',
  borderBottom: 'none',
}));
